/**
 * Member-app icons, vendored into public/vip/icons (static asset folder, not a route).
 * Black SVG fills tinted for the light green/white theme.
 */
import { VIP } from "@/lib/vipTheme";

export type VipIconName =
  | "active-leads"
  | "deals-closed"
  | "mods-progress"
  | "auction-clock"
  | "auction-alert"
  | "add-lead"
  | "mod-intake"
  | "wilder-ai" // legacy key, same icon as "ai-assistant"
  | "ai-assistant"
  | "training"
  | "security"
  | "home"
  | "analytics"
  | "comms"
  | "graph"
  | "trophy"
  | "check";

const SRC: Record<VipIconName, string> = {
  "active-leads": "/vip/icons/active-leads.svg",
  "deals-closed": "/vip/icons/deals-closed.svg",
  "mods-progress": "/vip/icons/mods-progress.svg",
  "auction-clock": "/vip/icons/auction-clock.svg",
  "auction-alert": "/vip/icons/auction-alert.svg",
  "add-lead": "/vip/icons/add-lead.svg",
  "mod-intake": "/vip/icons/mod-intake.svg",
  "wilder-ai": "/vip/icons/wilder-ai.svg",
  "ai-assistant": "/vip/icons/wilder-ai.svg",
  training: "/vip/icons/training.svg",
  security: "/vip/icons/security.svg",
  home: "/vip/icons/home.svg",
  analytics: "/vip/icons/analytics.svg",
  comms: "/vip/icons/comms.svg",
  graph: "/vip/icons/graph.svg",
  trophy: "/vip/icons/trophy.svg",
  check: "/vip/icons/check.svg",
};

export default function VipIcon({
  name,
  size = 28,
  tone = "silver",
  title,
}: {
  name: VipIconName;
  size?: number;
  tone?: "silver" | "white" | "blue" | "red" | "green" | "metal";
  title?: string;
}) {
  const filters: Record<string, string> = {
    // muted grey-green #5B6660
    silver: "brightness(0) saturate(100%) invert(38%) sepia(8%) saturate(420%) hue-rotate(95deg)",
    white: "brightness(0) invert(1)",
    // forest green #1E4D2B (was blue)
    blue: "brightness(0) saturate(100%) invert(23%) sepia(30%) saturate(900%) hue-rotate(90deg) brightness(0.9)",
    // rose #F43F5E (was red)
    red: "brightness(0) saturate(100%) invert(40%) sepia(80%) saturate(3000%) hue-rotate(325deg) brightness(1)",
    // success #16A34A
    green: "brightness(0) saturate(100%) invert(45%) sepia(90%) saturate(450%) hue-rotate(95deg)",
    // charcoal
    metal: "brightness(0) invert(0.1)",
  };

  return (
    <img
      src={SRC[name]}
      alt={title || name}
      width={size}
      height={size}
      draggable={false}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        filter: filters[tone] || filters.silver,
        flexShrink: 0,
        display: "block",
        userSelect: "none",
      }}
    />
  );
}

/** Logo mark — full color PNG, no invert */
export function VipLogoMark({ height = 48 }: { height?: number }) {
  return (
    <img
      src="/cep-logo.png"
      alt="California Equity Program"
      height={height}
      style={{
        height,
        width: "auto",
        maxWidth: height * 2.4,
        objectFit: "contain",
        display: "block",
        flexShrink: 0,
      }}
      draggable={false}
    />
  );
}

export { VIP };
